<template>
    <form
        class="relative rounded-xl border border-gray-200 bg-white p-4"
        :class="{ 'ring-2 ring-inset ring-blue-300': isDragging }"
        @submit.prevent="submit"
        @paste="handlePaste"
        @dragover.prevent="isDragging = true"
        @dragenter.prevent="isDragging = true"
        @dragleave="isDragging = false"
        @drop.prevent="onDrop"
    >
        <AiSlashCommandSuggester
            :open="slashOpen"
            :suggestions="slashSuggestions"
            :active-index="slashActiveIndex"
            @select="applySlashSuggestion"
            @hover="slashActiveIndex = $event"
        />

        <div v-if="skill" class="mb-2 flex items-center gap-2">
            <span
                class="inline-flex max-w-full items-center gap-1.5 truncate rounded-full bg-[#FF421E]/10 px-3 py-1 text-xs font-medium text-[#FF421E]"
            >
                <SparklesIcon class="h-3.5 w-3.5 shrink-0" />
                <span class="truncate">{{ skill.name }}</span>
                <button
                    type="button"
                    class="ml-0.5 shrink-0 cursor-pointer rounded-full p-0.5 transition hover:bg-[#FF421E]/20"
                    :aria-label="t('vue.ai_skill_detach')"
                    @click="detach"
                >
                    <XMarkIcon class="h-3 w-3" />
                </button>
            </span>
        </div>

        <TextAreaInput
            ref="textarea"
            v-model="message"
            :placeholder="t('vue.ai_chat_input_placeholder')"
            :rows="5"
            :disabled="disabled"
            :error="uploadError || ''"
            @keydown="onKeydown"
        />

        <!-- A refused command is answered with its usage line, so the message
             runs to several lines. It is rendered in flow rather than through
             the field's own error slot, which is absolutely positioned and
             would lay itself over the toolbar below. -->
        <p
            v-if="error"
            class="mt-2 flex items-start gap-1.5 text-xs leading-5 text-custom-alert-100"
        >
            <ExclamationCircleIcon class="mt-0.5 h-4 w-4 shrink-0" />
            <span class="min-w-0 break-words">{{ error }}</span>
        </p>

        <AiChatComposerImagePreview
            v-if="previewUrl"
            class="mt-3"
            :url="previewUrl"
            :filename="image?.name"
            :disabled="disabled"
            @remove="clear"
        />

        <div
            v-if="uploadProgress !== null && uploadProgress !== undefined"
            class="mt-3"
        >
            <div class="h-1 w-full overflow-hidden rounded-full bg-gray-100">
                <div
                    class="h-full rounded-full bg-blue-500"
                    :style="{ width: `${uploadProgress}%` }"
                ></div>
            </div>
            <p class="mt-1 text-xs text-gray-400">
                {{ t("vue.ai_chat_uploading_image") }} {{ uploadProgress }}%
            </p>
        </div>

        <div class="mt-3 flex items-center justify-between gap-2">
            <div class="flex items-center gap-2">
                <button
                    type="button"
                    class="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                    :disabled="disabled || image !== null || isCommandDraft"
                    :aria-label="t('vue.ai_chat_attach_image')"
                    :title="t('vue.ai_chat_attach_image')"
                    @click="fileInput?.click()"
                >
                    <PhotoIcon class="h-4 w-4" />
                    {{ t("vue.ai_chat_attach_image") }}
                </button>

                <div v-if="teamSkillsActive" class="relative">
                    <button
                        type="button"
                        class="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 transition hover:bg-gray-50"
                        :class="{ 'border-[#FF421E] text-[#FF421E]': skill }"
                        @click="togglePicker"
                    >
                        <SparklesIcon class="h-4 w-4" />
                        {{ t("vue.ai_skill_attach") }}
                    </button>

                    <div
                        v-if="pickerOpen"
                        class="absolute bottom-full left-0 z-20 mb-1 w-72 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
                    >
                        <div class="border-b border-gray-100 p-2">
                            <TextInput
                                v-model="skillSearch"
                                :placeholder="t('vue.ai_skill_search')"
                            />
                        </div>
                        <div class="max-h-56 overflow-y-auto p-1">
                            <p
                                v-if="loading"
                                class="px-2 py-3 text-center text-xs text-gray-400"
                            >
                                {{ t("vue.ai_skill_loading") }}
                            </p>
                            <template v-else-if="skills.length === 0">
                                <p
                                    class="px-2 py-3 text-center text-xs text-gray-400"
                                >
                                    {{ t("vue.ai_skill_none") }}
                                </p>
                                <inertia-link
                                    :href="route('ai-skills.index')"
                                    class="block rounded-md px-2 py-1.5 text-center text-xs font-medium text-[#FF421E] transition hover:bg-[#FF421E]/5"
                                >
                                    {{ t("vue.ai_skill_create") }}
                                </inertia-link>
                            </template>
                            <button
                                v-for="option in skills"
                                v-else
                                :key="option.id"
                                type="button"
                                class="block w-full cursor-pointer rounded-md px-2 py-1.5 text-left transition hover:bg-[#FF421E]/5"
                                @click="pick(option)"
                            >
                                <span
                                    class="block truncate text-sm font-medium text-gray-800"
                                >
                                    {{ option.name }}
                                </span>
                                <span
                                    v-if="option.summary"
                                    class="block truncate text-xs text-gray-400"
                                >
                                    {{ option.summary }}
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <AtomicButton
                color="yellow"
                size="sm"
                type="submit"
                :disabled="disabled || !canSend"
            >
                {{ t("vue.ai_chat_send") }}
            </AtomicButton>
        </div>

        <input
            ref="fileInput"
            type="file"
            class="hidden"
            :accept="accept"
            @change="selectFromInput"
        />
    </form>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref, watch } from "vue";
import { usePage } from "@inertiajs/vue3";
import axios from "axios";
import route from "ziggy-js";
import {
    ExclamationCircleIcon,
    PhotoIcon,
    SparklesIcon,
    XMarkIcon,
} from "@heroicons/vue/24/outline";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import { useTranslations } from "@/composables/useTranslations";
import {
    AI_CHAT_IMAGE_ACCEPT,
    useAiChatImageUpload,
} from "@/composables/useAiChatImageUpload";
import type {
    IAiSkillAttachment,
    IAiSkillListItem,
} from "@/types/AiChat/IAiSkill";
import type { IAiSlashCommandSuggestion } from "@/types/AiChat/IAiSlashCommand";
import AiSlashCommandSuggester from "@/Pages/AiChat/components/AiSlashCommandSuggester.vue";
import {
    isAiSlashCommandDraft,
    useAiSlashCommands,
} from "@/composables/useAiSlashCommands";

const props = withDefaults(
    defineProps<{
        disabled: boolean;
        error?: string;
        skill: IAiSkillAttachment | null;
        uploadProgress?: number | null;
    }>(),
    { error: "", uploadProgress: null },
);

const emit = defineEmits<{
    (event: "send", payload: { message: string; image: File | null }): void;
    (event: "update:skill", skill: IAiSkillAttachment | null): void;
}>();

const { t } = useTranslations();
const teamSkillsActive = computed(
    () => usePage<IRhizomPageProps>().props.inAppAiChatTeamSkillsActive,
);
const message = ref("");
const textarea = ref<{ focus: () => void } | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);
const isDragging = ref(false);
const accept = AI_CHAT_IMAGE_ACCEPT;

const {
    image,
    previewUrl,
    error: uploadError,
    selectFromInput,
    handlePaste,
    handleDrop,
    clear,
} = useAiChatImageUpload();

const canSend = computed(
    () => message.value.trim() !== "" || image.value !== null,
);

// The "/" suggester. It offers "/tool", then the commands, then the parameters
// each one still needs — all read from the server-owned catalogue.
const {
    suggestions: slashSuggestions,
    open: slashOpen,
    activeIndex: slashActiveIndex,
    apply: applySlash,
    handleKeydown: handleSlashKeydown,
} = useAiSlashCommands(message);

const isCommandDraft = computed(() => isAiSlashCommandDraft(message.value));

const applySlashSuggestion = async (
    suggestion: IAiSlashCommandSuggestion,
): Promise<void> => {
    applySlash(suggestion);
    await nextTick();
    textarea.value?.focus();
};

const onDrop = (event: DragEvent) => {
    isDragging.value = false;
    handleDrop(event);
};

const pickerOpen = ref(false);
const skills = ref<IAiSkillListItem[]>([]);
const skillSearch = ref("");
const loading = ref(false);
let searchTimer: number | undefined;

const loadSkills = async (): Promise<void> => {
    loading.value = true;
    try {
        const response = await axios.get(route("ai-chat.api.skills"), {
            params: { q: skillSearch.value },
        });
        skills.value = response.data.items ?? [];
    } catch {
        skills.value = [];
    } finally {
        loading.value = false;
    }
};

const togglePicker = (): void => {
    pickerOpen.value = !pickerOpen.value;
    if (pickerOpen.value) {
        void loadSkills();
    }
};

watch(skillSearch, () => {
    if (searchTimer !== undefined) {
        window.clearTimeout(searchTimer);
    }
    searchTimer = window.setTimeout(() => void loadSkills(), 300);
});

const pick = (option: IAiSkillListItem): void => {
    emit("update:skill", { id: option.id, name: option.name });
    pickerOpen.value = false;
};

const detach = (): void => {
    emit("update:skill", null);
};

const submit = () => {
    if (props.disabled || !canSend.value) {
        return;
    }

    // The parent decides when to clear the composer: immediately when an
    // optimistic bubble takes over, or only after a successful server
    // round-trip when a new chat (with a possible image upload) is created.
    emit("send", { message: message.value.trim(), image: image.value });
};

const reset = (): void => {
    message.value = "";
    clear();
};

/**
 * Drops a ready-made question into the field and hands the caret over, so a
 * suggestion the user picked elsewhere can still be amended before it is sent.
 * An unsent draft is kept: the suggestion is appended rather than overwriting it.
 */
const fill = async (text: string): Promise<void> => {
    const draft = message.value.trim();
    message.value = draft === "" ? text : `${draft}\n${text}`;

    await nextTick();
    textarea.value?.focus();
};

/**
 * Replaces the draft outright. A command line has to stand alone, so unlike
 * fill() this never appends to what is already there.
 */
const replaceWith = async (text: string): Promise<void> => {
    message.value = text;

    await nextTick();
    textarea.value?.focus();
};

defineExpose({ reset, fill, replaceWith });

const onKeydown = (event: KeyboardEvent): void => {
    // The popover gets first refusal on the keys it owns; anything it does not
    // consume still sends the message exactly as before.
    if (slashOpen.value) {
        const consumed = handleSlashKeydown(event);

        if (consumed) {
            event.preventDefault();
            if (event.key === "Enter" || event.key === "Tab") {
                void nextTick().then(() => textarea.value?.focus());
            }
            return;
        }
    }

    if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        submit();
    }
};
</script>
